import { el, type Nav, type Screen } from "./dom";
import { buildTopbar } from "./topbar";
import { i18n, t } from "../i18n";
import { PUZZLES, dailyPuzzleId, getPuzzle } from "../game/puzzles";
import { todayKey } from "../game/daily";
import { progress } from "../store/progress";

function starRow(filled: number): string {
  let s = "";
  for (let i = 1; i <= 3; i++) s += `<span class="star ${i <= filled ? "on" : ""}">★</span>`;
  return s;
}

/**
 * Puzzle mode list (idea #8): handcrafted deterministic boards, sequentially
 * unlocked, plus a rotating "puzzle of the day" card at the top.
 */
export class PuzzleScreen implements Screen {
  private off?: () => void;
  constructor(private nav: Nav) {}

  mount(host: HTMLElement): void {
    const screen = el("div", "screen puzzles");
    const top = buildTopbar({
      title: "menu.puzzles",
      onBack: () => this.nav.home(),
      showStardust: true,
    });

    const body = el("div", "puzzle-body");

    const render = (): void => {
      top.refresh();
      body.innerHTML = "";

      // Puzzle of the day — always playable, rotates deterministically.
      const dailyId = dailyPuzzleId(todayKey());
      const dailyDef = getPuzzle(dailyId);
      if (dailyDef) {
        const card = el("button", "puzzle-daily-card");
        card.innerHTML = `
          <span class="pd-icon">🧩</span>
          <span class="pd-info">
            <span class="pd-title">${t("puzzle.daily")}</span>
            <span class="pd-sub">${t("puzzle.dailySub", { n: dailyDef.order })}</span>
          </span>
          <span class="pd-go">▶</span>`;
        card.onclick = () => this.nav.playPuzzle(dailyId);
        body.appendChild(card);
      }

      // Sequential puzzle list. Each unlocks when its predecessor is cleared
      // at the puzzle's own (fixed) board width.
      const list = el("div", "puzzle-list");
      for (const p of PUZZLES) {
        const unlocked = progress.isUnlocked(p.id, p.unlock?.requiresStage, p.gridCols);
        const stars = progress.starsFor(p.id, p.gridCols);
        const cell = el("button", `puzzle-cell ${unlocked ? "" : "locked"}`);
        cell.innerHTML = `
          <span class="puzzle-no">${p.order}</span>
          <span class="puzzle-name">${t(`puzzle.${p.id}`)}</span>
          ${unlocked ? `<span class="stage-stars">${starRow(stars)}</span>` : `<span class="lock">🔒</span>`}`;
        if (unlocked) cell.onclick = () => this.nav.playPuzzle(p.id);
        else cell.disabled = true;
        list.appendChild(cell);
      }
      body.appendChild(list);
    };

    screen.append(top.node, body);
    host.appendChild(screen);
    render();
    this.off = i18n.onChange(render);
  }

  unmount(): void {
    this.off?.();
  }
}
